/**
 *
 * Onboard Azure Account
 *
 */

import React, { FC, useCallback, useReducer } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { CloudAccountGetResponse } from '@ariksa/cloud-account/api';
import { Box, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { PageHeaderWithIcon } from 'components/DataDisplay';
import { Form, FormSchema, textFieldStyles } from 'components/DataEntry/Form';
import {
  CheckmarkCircleIcon,
  CopyIcon,
  ThirdPartyIcon,
} from 'components/Icons';
import { errorToast } from 'components/Toast';
import {
  azureHelpHeaderMapping,
  renderHelp,
} from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/MicrosoftAzure/Help/utils';
import { AzureOnboardingHelp } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/MicrosoftAzure/types';
import { renderRequiredSteps } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardGcp/utils';
import { ReadWriteTag } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/ReadOnlyTag';
import { stopSignWithMessage } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { selectCloudAccountWizard } from 'containers/Setup/CloudAccounts/CloudAccountWizard/selectors';
import { formStyles } from 'containers/Setup/CloudAccounts/CloudAccountWizard/styles';
import { actions } from 'containers/Setup/CloudAccounts/slice';

export const EnableRemediationForAzure: FC<{
  account: CloudAccountGetResponse;
  onClose();
}> = props => {
  const { account, onClose } = props;
  const dispatch = useDispatch();
  const { onboardingAccount } = useSelector(selectCloudAccountWizard);
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      applicationId: '',
      tenantId: '',
      secret: '',
      isConfirm: false,
      scriptExecuted: false,
      showHelp: AzureOnboardingHelp.None,
    },
  );
  const remediationScript = `curl -sL https://sm-test-434.s3.us-east-1.amazonaws.com/azure_remediation_setup.sh | bash -s -- --scope `;

  const handleSubmit = data => {
    const { secret, tenantId, applicationId } = data;

    const onError = err => {
      errorToast({ title: err.title, description: err.description });
    };

    dispatch(
      actions.onboardAccountForRemediation({
        q: {
          cloudType: CloudProviders.Azure,
          uuid: account.uuid,
          bodyOnboardRemediationAccountV1CloudAccountAccountRemediationPost: {
            remediation_account_config: {
              azure_remediation_config: {
                secret: secret,
                tenant_id: tenantId,
                subscription_id: account.cloud_account_id,
                application_id: applicationId,
              },
            },
          },
        },
        onSuccess: () => onClose(),
        onError,
      }),
    );
  };
  const handleReset = () => {
    onClose();
  };

  const toggleHelp = useCallback(
    (helpType: AzureOnboardingHelp) => {
      updateState({
        showHelp:
          state.showHelp === helpType ? AzureOnboardingHelp.None : helpType,
      });
    },
    [state.showHelp],
  );

  const steps = [
    {
      label: 'Execute script to enable remediation',
      helpHeader: 'Automatically prep Azure Subscription to enable remediation',
      headerIcon: <CheckmarkCircleIcon />,
      iconBgColor: 'green.300',
      helpContent: (
        <Stack spacing={4}>
          <Box fontWeight={600} fontSize="md">
            Execute script to enable remediation
          </Box>
          <Box h={32} bg={customTheme.colors.gray['50']} p={4} borderRadius={6}>
            {remediationScript + account.cloud_account_id}
          </Box>
        </Stack>
      ),
      secondary: [
        {
          label: 'Copy script command to execute on Azure CLI',
          icon: <CopyIcon p={0.5} />,
          tooltip: 'Copy command',
          copyText: remediationScript + account.cloud_account_id,
        },
        {
          label: 'Redirect to Azure console and open Azure CLI',
          icon: <ThirdPartyIcon />,
          url: 'https://portal.azure.com/#cloudshell/',
          tooltip: 'Redirect to Azure console. Manually execute command',
        },
      ],
    },
  ];

  const commonParams = {
    styles: {
      label: {
        ...textFieldStyles.label,
        justifyContent: 'space-between',
      },
    },
    helpIcon: renderHelp(),
    onClickHelpIcon: () => toggleHelp(AzureOnboardingHelp.InputParameters),
  };

  const inputParams: FormSchema = !!state.scriptExecuted
    ? {
        tenantId: {
          type: 'text',
          label: 'Directory (Tenant) ID',
          placeholder: 'abcddf01-2945-6789-0a8c-def089345678',
          value: state.tenantId,
          isRequired: true,
          onChange: value => updateState({ tenantId: value }),
          ...commonParams,
        },
        applicationId: {
          type: 'text',
          label: 'Application (Client) ID',
          placeholder: 'fsdddf01-1245-1289-128c-12308934qasw',
          value: state.applicationId,
          isRequired: true,
          onChange: value => updateState({ applicationId: value }),
          ...commonParams,
        },
        secret: {
          type: 'text',
          label: 'Secret',
          placeholder: '******************',
          value: state.secret,
          htmlInputType: 'password',
          isRequired: true,
          onChange: value => updateState({ secret: value }),
          ...commonParams,
        },
      }
    : {};

  return (
    <>
      <Form
        schema={{
          accountName: {
            type: 'text',
            label: 'Connector Name',
            placeholder: 'Enter connector name',
            isRequired: true,
            isDisabled: true,
            defaultValue: account?.name,
          },
          cfs_template_type_info: {
            type: 'custom-with-form-control',
            label: 'Onboarding mode',
            component: <ReadWriteTag />,
          },
          subscriptionId: {
            type: 'text',
            label: 'Subscription ID',
            value: account.cloud_account_id!,
            isRequired: true,
            isDisabled: true,
          },
          confirmMessage: {
            type: 'custom',
            component: () =>
              stopSignWithMessage(
                'Review requirements and execute these steps before proceeding further',
                'Confirm that you have permissions to enable remediation',
                state.isConfirm,
                val => {
                  updateState({ isConfirm: val });
                },
                () =>
                  toggleHelp(AzureOnboardingHelp.ManagementGroupPermissions),
                false,
              ),
          },
          steps: {
            type: 'custom',
            component: () =>
              renderRequiredSteps({
                steps,
                onClickButton: () =>
                  toggleHelp(AzureOnboardingHelp.Remediation),
                copied: state.copied,
                setCopied: val => updateState({ copied: val }),
                isDisabled: !state.isConfirm,
                isChecked: state.scriptExecuted,
                setIsChecked: val => updateState({ scriptExecuted: val }),
              }),
          },
          ...inputParams,
        }}
        helpDrawer={{
          isOpen: state.showHelp !== AzureOnboardingHelp.None,
          onClose: () => updateState({ showHelp: AzureOnboardingHelp.None }),
          content: azureHelpHeaderMapping(
            state.showHelp,
            account?.cloud_account_id ?? '',
          )?.content,
          header: (
            <PageHeaderWithIcon
              label={
                azureHelpHeaderMapping(
                  state.showHelp,
                  account?.cloud_account_id ?? '',
                )?.header
              }
              icon={
                azureHelpHeaderMapping(
                  state.showHelp,
                  account?.cloud_account_id ?? '',
                )?.icon
              }
              reversed
              fontWeight={600}
              fontSize="lg"
            />
          ),
          styles: { body: { pt: 6 } },
        }}
        buttonOptions={{
          submit: {
            name: 'Connect',
            isLoading: onboardingAccount.isLoading,
            isDisabled:
              !state.secret ||
              !state.applicationId ||
              !state.tenantId ||
              !state.isConfirm ||
              !state.scriptExecuted ||
              onboardingAccount.isLoading,
          },
          reset: {
            name: 'Cancel',
            isVisible: true,
            onClick: handleReset,
          },
        }}
        styles={formStyles()}
        handleSubmit={handleSubmit}
      />
    </>
  );
};
